import React, { Fragment, useEffect, useState, useRef } from "react";
import { bool, arrayOf, string } from "prop-types";
import { connect, useDispatch } from "react-redux";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomData from "pages/_components/CustomData/CustomData";
import Image from "pages/_components/Image";
import Button from "pages/_components/Button";
import { replace } from "connected-react-router";
import MainContainer from "pages/_components/MainContainer";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { Col, Row } from "react-bootstrap";
import { ResponsiveHeader } from "pages/_components/header";
import Checkbox from "pages/_components/Checkbox";
import EnvironmentHelpSlide from "../_components/EnvironmentHelpSlide";
import EnvironmentStepper from "../_components/EnvironmentStepper";
import PaywayTitle from "../_components/PaywayTitle";
import { useHistoricData } from "../hooks/useHistoricData";

const PAGE_ID = "StepSelectPaywayTerminalType";
const FORM_ID = "payway.select.terminal";

const StepSelectPaywayTerminalType = ({ isMobile, isDesktop, paywayTerminalTypeBackup, fetching }) => {
    const formRef = useRef(null);
    const dispatch = useDispatch();
    const [helpIsVisible, setHelpIsVisible] = useState(false);
    const [terminalType, setTerminalType] = useState(null);

    useEffect(() => {
        dispatch(environmentActions.restartEnvironmentStore());
    }, []);

    const noBackupData = true;
    const { getBackupDataByStepId } = useHistoricData(!noBackupData);

    const onHeaderBack = () => {
        getBackupDataByStepId("stepPaywayFirst");
        dispatch(replace("/environment/stepPaywayFirst"));
    };

    const getInitialTerminalTypeValue = (type) =>
        paywayTerminalTypeBackup !== null &&
        paywayTerminalTypeBackup?.terminalTypes &&
        paywayTerminalTypeBackup?.terminalTypes.includes(type);

    useEffect(() => {
        if (fetching) {
            return;
        }
        formRef.current.setFieldValue("onsite", getInitialTerminalTypeValue("PRESENCIAL"));
        formRef.current.setFieldValue("online", getInitialTerminalTypeValue("ONLINE"));
    }, [fetching, paywayTerminalTypeBackup]);

    const formikProps = {
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            onsite: getInitialTerminalTypeValue("PRESENCIAL"),
            online: getInitialTerminalTypeValue("ONLINE"),
        },
        validationSchema: () =>
            Yup.object().shape({
                onsite: Yup.bool(),
                online: Yup.bool(),
            }),
        onSubmit: (values) => {
            const { onsite, online } = values;
            const terminalTypes = [];
            if (onsite) {
                terminalTypes.push("PRESENCIAL");
            }
            if (online) {
                terminalTypes.push("ONLINE");
            }
            dispatch(environmentActions.setPaywayTerminalTypeBackup(terminalTypes));
            dispatch(environmentActions.savePaywayTerminalType(terminalTypes));
        },
    };

    const setHelp = (value, type) => {
        setHelpIsVisible(value);
        setTerminalType(type);
    };

    const submitIsAllowed = (allFormik) => {
        const { online, onsite } = allFormik.values;
        return online || onsite;
    };

    return (
        <>
            <ResponsiveHeader onClickBack={onHeaderBack} id="legalPerson.title" showTitle={isMobile} />
            <MainContainer className="onboarding" showLoader={fetching}>
                <EnvironmentStepper isMobile={isMobile} page={PAGE_ID} />
                <div className="step-container">
                    <PaywayTitle handleHelp={() => setHelp(true, null)} isDesktop={isDesktop} />

                    <div className="illustration-alta-persona flex justify-center">
                        <Image src="images/figma/illustrations/onboardingPayway.svg" className="svg-image" />
                    </div>
                    <div className="py-6" />
                    <div className="box box-border-soft payway-box">
                        <p>
                            <CustomData titleText="payway.select.terminal.type.label" titleTextType="I18N" />
                        </p>
                        <Formik {...formikProps} ref={formRef}>
                            {(allFormik) => (
                                <Form>
                                    <div className="payway-terminal-types-container">
                                        <div>
                                            <Field component={Checkbox} idForm={FORM_ID} name="onsite" />
                                            <Button
                                                className="head__icon-button"
                                                bsStyle="link"
                                                image="images/figma/icon/help.svg"
                                                block={false}
                                                onClick={() => setHelp(true, "onsite")}
                                                style={{ padding: "0 0 0 20px" }}
                                            />
                                        </div>
                                        <div className="payway-container-second-item">
                                            <Field component={Checkbox} idForm={FORM_ID} name="online" />
                                            <Button
                                                className="head__icon-button payway-second-help-icon"
                                                bsStyle="link"
                                                image="images/figma/icon/help.svg"
                                                block={false}
                                                onClick={() => setHelp(true, "online")}
                                            />
                                        </div>
                                    </div>
                                    <Row className="form-actions no-print">
                                        <Col xs={12} md={4} mdOffset={4}>
                                            <Button
                                                className="btn-primary"
                                                label="global.continue"
                                                loading={false}
                                                type="submit"
                                                block
                                                disabled={!submitIsAllowed(allFormik)}
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </MainContainer>

            <EnvironmentHelpSlide
                slideIsVisible={helpIsVisible}
                closeSlide={() => setHelp(false, null)}
                isMobile={isMobile}
                page={PAGE_ID}
                paywayTerminalType={terminalType}
                isDesktop={isDesktop}
            />
        </>
    );
};

StepSelectPaywayTerminalType.propTypes = {
    isDesktop: bool.isRequired,
    isMobile: bool.isRequired,
    fetching: bool.isRequired,
    paywayTerminalTypeBackup: arrayOf(string).isRequired,
};
const mapStateToProps = (state) => ({
    fetching: environmentSelectors.getFetching(state),
    paywayTerminalTypeBackup: environmentSelectors.getPaywayTerminalTypeBackup(state),
});

export default connect(mapStateToProps)(StepSelectPaywayTerminalType);
